/**
 * AETERNA Session Store
 *
 * 管理用户认证状态：token、用户信息、登录/登出/刷新。
 * Token 保存在内存 + localStorage，同一浏览器下跨 Tab 保持登录态。
 *
 * SECURITY NOTE: bearer token storage
 * 后端使用短 TTL + jti 会话表 + refresh rotation + logout revoke。
 * 本地登出时会清理 localStorage token，避免残留。
 */

import { create } from "zustand";
import { http, setAuthHandlers } from "../api/http";
import { isApiEnabled } from "../api/config";
import { profileApi } from "../features/profile/api";
import type { AuthCaptchaProof } from "../pages/auth/auth-captcha";

// ── 类型 ──

interface User {
  id: string;
  name: string;
  role: string;
}

interface SessionState {
  /** JWT token */
  token: string | null;
  /** 当前用户 */
  user: User | null;
  /** 是否已认证 */
  isAuthenticated: boolean;
  /** 启动时的加载状态 */
  isLoading: boolean;
  /** 错误信息 */
  error: string | null;
  /** 是否已完成 onboarding */
  onboardingCompleted: boolean;
}

interface SessionActions {
  /** 发送验证码 */
  sendCode: (phone: string, captcha?: AuthCaptchaProof) => Promise<void>;
  /** 手机号验证码登录，返回是否为新用户 */
  login: (
    phone: string,
    code: string,
  ) => Promise<{ isNewUser: boolean; onboardingCompleted: boolean }>;
  /** 用邀请码创建短期注册会话 */
  createInviteSession: (
    code: string,
  ) => Promise<{ inviteSessionToken: string; expiresAt: string }>;
  /** 发送邀请注册验证码 */
  sendRegisterCode: (
    phone: string,
    inviteSessionToken: string,
    captcha?: AuthCaptchaProof,
  ) => Promise<void>;
  /** 使用邀请注册会话完成注册 */
  registerWithInvite: (
    phone: string,
    code: string,
    inviteSessionToken: string,
  ) => Promise<{ isNewUser: boolean; onboardingCompleted: boolean }>;
  /** Token 刷新 */
  refresh: () => Promise<boolean>;
  /** 登出 */
  logout: () => void;
  /** App 启动时恢复会话 */
  bootstrap: () => Promise<void>;
  /** 清除错误 */
  clearError: () => void;
  /** 标记 onboarding 已完成 */
  completeOnboarding: (data?: Record<string, unknown>) => Promise<void>;
  /** 飞书桥接绑定 */
  bindFeishu: (
    phone: string,
    code: string,
    nonce: string,
  ) => Promise<{ isNewUser: boolean; onboardingCompleted: boolean }>;
}

type SessionStore = SessionState & SessionActions;

// ── Session token storage ──

const TOKEN_KEY = "aeterna_token";
const LEGACY_ONBOARDING_KEY = "aeterna_onboarding_done";
const ONBOARDING_KEY_PREFIX = "aeterna_onboarding_done:";

function onboardingKey(userId: string) {
  return `${ONBOARDING_KEY_PREFIX}${userId}`;
}

function loadOnboardingDone(userId?: string | null): boolean {
  if (!userId) return false;
  try {
    return localStorage.getItem(onboardingKey(userId)) === "1";
  } catch {
    return false;
  }
}

function saveOnboardingDone(userId?: string | null) {
  if (!userId) return;
  try {
    localStorage.setItem(onboardingKey(userId), "1");
    localStorage.removeItem(LEGACY_ONBOARDING_KEY);
  } catch {
    /* */
  }
}

function clearLegacyOnboardingDone() {
  try {
    localStorage.removeItem(LEGACY_ONBOARDING_KEY);
  } catch {
    /* */
  }
}

let bootstrapPromise: Promise<void> | null = null;

async function resolveOnboardingCompleted(userId: string): Promise<boolean> {
  let onboardingCompleted = loadOnboardingDone(userId);
  if (isApiEnabled("profile")) {
    try {
      const profile = await profileApi.getMe();
      onboardingCompleted =
        Boolean(profile.onboardingCompleted) || onboardingCompleted;
      if (onboardingCompleted) saveOnboardingDone(userId);
    } catch {
      // 保持本地 per-user fallback
    }
  }
  return onboardingCompleted;
}

function saveToken(token: string) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // storage 不可用时静默失败，内存态仍可继续当前会话
  }
}

function loadToken(): string | null {
  try {
    const localToken = localStorage.getItem(TOKEN_KEY);
    if (localToken) return localToken;

    const legacyToken = sessionStorage.getItem(TOKEN_KEY);
    if (legacyToken) {
      localStorage.setItem(TOKEN_KEY, legacyToken);
      sessionStorage.removeItem(TOKEN_KEY);
    }
    return legacyToken;
  } catch {
    return null;
  }
}

function clearToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // 静默
  }
}

// ── Store ──

export const useSessionStore = create<SessionStore>((set, get) => ({
  // State
  token: null,
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  onboardingCompleted: false,

  // Actions
  sendCode: async (phone: string, captcha?: AuthCaptchaProof) => {
    set({ error: null });

    if (!isApiEnabled("auth")) {
      // Mock 模式：模拟发送成功
      return;
    }

    try {
      await http.post(
        "/auth/login/sms/send",
        {
          phone,
          captchaToken: captcha?.captchaToken ?? "",
          captchaSessionId: captcha?.captchaSessionId ?? "",
        },
        { skipAuth: true },
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : "发送验证码失败";
      set({ error: message });
      throw err;
    }
  },

  login: async (phone: string, code: string) => {
    set({ error: null, isLoading: true });

    if (!isApiEnabled("auth")) {
      // Mock 模式：模拟老用户登录成功
      const mockUser = {
        id: "mock-user-001",
        name: `用户${phone.slice(-4)}`,
        role: "user",
      };
      const onboardingCompleted = loadOnboardingDone(mockUser.id);
      set({
        token: "mock-token",
        user: mockUser,
        isAuthenticated: true,
        isLoading: false,
        onboardingCompleted,
      });
      saveToken("mock-token");
      clearLegacyOnboardingDone();
      return { isNewUser: false, onboardingCompleted };
    }

    try {
      const result = await http.post<{
        session: { token: string; expiresAt: string };
        user: User;
        isNewUser: boolean;
      }>("/auth/login/phone", { phone, code }, { skipAuth: true });

      const token = result.session.token;
      set({ token, user: result.user, isAuthenticated: true });
      saveToken(token);
      clearLegacyOnboardingDone();
      const onboardingCompleted = await resolveOnboardingCompleted(
        result.user.id,
      );
      set({ isLoading: false, onboardingCompleted });
      return { isNewUser: result.isNewUser, onboardingCompleted };
    } catch (err) {
      set({ isLoading: false });
      const message = err instanceof Error ? err.message : "登录失败";
      set({ error: message });
      throw err;
    }
  },

  createInviteSession: async (code: string) => {
    set({ error: null });

    if (!isApiEnabled("auth")) {
      return {
        inviteSessionToken: `mock-invite-session-${code}`,
        expiresAt: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
      };
    }

    try {
      return await http.post<{ inviteSessionToken: string; expiresAt: string }>(
        "/auth/invitations/session",
        { code },
        { skipAuth: true },
      );
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "邀请链接无效或已过期";
      set({ error: message });
      throw err;
    }
  },

  sendRegisterCode: async (
    phone: string,
    inviteSessionToken: string,
    captcha?: AuthCaptchaProof,
  ) => {
    set({ error: null });

    if (!isApiEnabled("auth")) {
      return;
    }

    try {
      await http.post(
        "/auth/register/sms/send",
        {
          phone,
          inviteSessionToken,
          captchaToken: captcha?.captchaToken ?? "",
          captchaSessionId: captcha?.captchaSessionId ?? "",
        },
        { skipAuth: true },
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : "发送验证码失败";
      set({ error: message });
      throw err;
    }
  },

  registerWithInvite: async (
    phone: string,
    code: string,
    inviteSessionToken: string,
  ) => {
    set({ error: null, isLoading: true });

    if (!isApiEnabled("auth")) {
      const mockUser = {
        id: "mock-invite-user-001",
        name: `用户${phone.slice(-4)}`,
        role: "user",
      };
      const onboardingCompleted = loadOnboardingDone(mockUser.id);
      set({
        token: "mock-token",
        user: mockUser,
        isAuthenticated: true,
        isLoading: false,
        onboardingCompleted,
      });
      saveToken("mock-token");
      clearLegacyOnboardingDone();
      return { isNewUser: true, onboardingCompleted };
    }

    try {
      const result = await http.post<{
        session: { token: string; expiresAt: string };
        user: User;
        isNewUser: boolean;
      }>(
        "/auth/register/phone",
        { phone, code, inviteSessionToken },
        { skipAuth: true },
      );

      const token = result.session.token;
      set({ token, user: result.user, isAuthenticated: true });
      saveToken(token);
      clearLegacyOnboardingDone();
      const onboardingCompleted = await resolveOnboardingCompleted(
        result.user.id,
      );
      set({ isLoading: false, onboardingCompleted });
      return { isNewUser: result.isNewUser, onboardingCompleted };
    } catch (err) {
      set({ isLoading: false });
      const message = err instanceof Error ? err.message : "注册失败";
      set({ error: message });
      throw err;
    }
  },

  bindFeishu: async (phone: string, code: string, nonce: string) => {
    set({ error: null, isLoading: true });

    if (!isApiEnabled("auth")) {
      const mockUser = {
        id: "mock-feishu-001",
        name: `飞书桥接用户${phone.slice(-4)}`,
        role: "user",
      };
      const onboardingCompleted = loadOnboardingDone(mockUser.id);
      set({
        token: "mock-token",
        user: mockUser,
        isAuthenticated: true,
        isLoading: false,
        onboardingCompleted,
      });
      saveToken("mock-token");
      clearLegacyOnboardingDone();
      return { isNewUser: false, onboardingCompleted };
    }

    try {
      const result = await http.post<{
        session: { token: string; expiresAt: string };
        user: User;
        isNewUser: boolean;
      }>("/auth/feishu/bind", { phone, code, nonce }, { skipAuth: true });

      const token = result.session.token;
      set({ token, user: result.user, isAuthenticated: true });
      saveToken(token);
      clearLegacyOnboardingDone();
      const onboardingCompleted = await resolveOnboardingCompleted(
        result.user.id,
      );
      set({ isLoading: false, onboardingCompleted });
      return { isNewUser: result.isNewUser, onboardingCompleted };
    } catch (err) {
      set({ isLoading: false });
      const message = err instanceof Error ? err.message : "桥接失败";
      set({ error: message });
      throw err;
    }
  },

  refresh: async () => {
    const { token } = get();
    if (!token || !isApiEnabled("auth")) return false;

    try {
      const result = await http.post<{ token: string; expiresAt: string }>(
        "/auth/refresh",
        undefined,
        { skipRetry: true, skip401: true },
      );
      const newToken = result.token;
      set({ token: newToken });
      saveToken(newToken);
      return true;
    } catch {
      return false;
    }
  },

  logout: () => {
    const token = get().token;
    if (token && isApiEnabled("auth") && token !== "mock-token") {
      void http
        .post("/auth/logout", undefined, { skipRetry: true, skip401: true })
        .catch(() => {});
    }
    set({
      token: null,
      user: null,
      isAuthenticated: false,
      error: null,
      onboardingCompleted: false,
    });
    clearToken();
  },

  bootstrap: async () => {
    if (bootstrapPromise) return bootstrapPromise;
    bootstrapPromise = (async () => {
      const savedToken = loadToken();
      if (!savedToken) {
        clearLegacyOnboardingDone();
        set({ isLoading: false });
        return;
      }

      // Mock 模式：直接恢复
      if (!isApiEnabled("auth")) {
        const mockUser = { id: "mock-user-001", name: "体验用户", role: "user" };
        set({
          token: savedToken,
          user: mockUser,
          isAuthenticated: true,
          isLoading: false,
          onboardingCompleted: loadOnboardingDone(mockUser.id),
        });
        clearLegacyOnboardingDone();
        return;
      }

      // 真实模式：用存储的 token 调 /auth/me 验证
      set({ token: savedToken });
      try {
        const user = await http.get<User>("/auth/me");
        const onboardingCompleted = await resolveOnboardingCompleted(user.id);
        clearLegacyOnboardingDone();

        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          onboardingCompleted,
        });
      } catch (err) {
        const status =
          err instanceof Error && "status" in err
            ? (err as { status: number }).status
            : 0;
        if (status === 401 || status === 403) {
          clearToken();
          set({
            token: null,
            isAuthenticated: false,
            isLoading: false,
            onboardingCompleted: false,
          });
        } else {
          set({ isAuthenticated: true, isLoading: false });
        }
      }
    })();

    try {
      return await bootstrapPromise;
    } finally {
      bootstrapPromise = null;
    }
  },

  clearError: () => set({ error: null }),

  completeOnboarding: async (data?: Record<string, unknown>) => {
    const userId = get().user?.id;
    saveOnboardingDone(userId);
    set({ onboardingCompleted: true });

    if (isApiEnabled("profile") && get().isAuthenticated) {
      try {
        await profileApi.updateMe({
          onboardingCompleted: true,
          onboardingData: data,
        });
      } catch (err) {
        console.warn("[SessionStore] 同步 onboarding 状态到后端失败:", err);
      }
    }
  },
}));

// ── 注册 auth handlers 到 http 客户端 ──

setAuthHandlers(
  () => useSessionStore.getState().token,
  () => useSessionStore.getState().refresh(),
  () => useSessionStore.getState().logout(),
);
